/**
 * Types mirroring the Stonker gateway's ACTUAL responses (source of truth:
 * workers/gateway/src/publicApi.ts + admin.ts + db.ts).
 *
 * Public API:  GET /api/v1/*   — raw payloads (no envelope), 30s edge cache,
 *                                `X-Stonker-Cache: kv|origin` header.
 * Admin API:   /admin/api/*    — Cloudflare Access-guarded in production.
 *
 * Errors on both surfaces are non-2xx with body `{ error: string, ...extra }`.
 * Timestamps are epoch milliseconds throughout (D1 integer columns).
 */

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

export type Address = string
export type EpochMs = number

/** Non-2xx gateway body. */
export type GatewayError = { error: string; [k: string]: unknown }

// ---------------------------------------------------------------------------
// GET /api/v1/health
// ---------------------------------------------------------------------------

export type HealthResponse = {
  ok: boolean
  service: string
  environment: string
  db: boolean
  ts: EpochMs
}

// ---------------------------------------------------------------------------
// GET /api/v1/portfolio/current  -> { state }
// GET /api/v1/portfolio/history  -> { items }
// ---------------------------------------------------------------------------

export type PortfolioStateStatus = 'target' | 'live' | 'superseded' | 'aborted'

/**
 * `positions` is stored by the agent as
 * `{ cashUsd: number, [tokenAddress]: { qty: number, priceUsd: number } }`
 * but the gateway passes the JSON through untyped — use `parsePositions`.
 */
export type PortfolioState = {
  id: string
  asOf: EpochMs
  status: PortfolioStateStatus
  /** tokenAddress -> weight (0..1). Cash weight is implicit: 1 - sum. */
  weights: Record<Address, number>
  positions: Record<string, unknown> | null
  navUsd: number | null
  optimizerMeta: Record<string, unknown> | null
  workflowId: string | null
  prevStateId: string | null
  aiSummary: string | null
  updatedAt: EpochMs
}

export type PortfolioCurrentResponse = { state: PortfolioState | null }

export type PortfolioHistoryItem = {
  id: string
  asOf: EpochMs
  status: PortfolioStateStatus
  navUsd: number | null
  weights: Record<Address, number>
}

export type PortfolioHistoryResponse = { items: PortfolioHistoryItem[] }

/** Client-side view of the agent's positions JSON (defensively parsed). */
export type ParsedPositions = {
  cashUsd: number | null
  tokens: { address: Address; qty: number; priceUsd: number }[]
}

export function parsePositions(
  positions: Record<string, unknown> | null,
): ParsedPositions {
  const out: ParsedPositions = { cashUsd: null, tokens: [] }
  if (!positions) return out
  for (const [key, value] of Object.entries(positions)) {
    if (key === 'cashUsd') {
      if (typeof value === 'number' && Number.isFinite(value)) {
        out.cashUsd = value
      }
      continue
    }
    if (value !== null && typeof value === 'object') {
      const qty = (value as { qty?: unknown }).qty
      const priceUsd = (value as { priceUsd?: unknown }).priceUsd
      if (
        typeof qty === 'number' &&
        Number.isFinite(qty) &&
        typeof priceUsd === 'number' &&
        Number.isFinite(priceUsd)
      ) {
        out.tokens.push({ address: key, qty, priceUsd })
      }
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// GET /api/v1/performance -> { performance }
// (shape from workers/gateway/src/performance.ts; null until >=1 NAV point)
// ---------------------------------------------------------------------------

export type PerformancePoint = {
  t: EpochMs
  nav: number
  /** Cumulative return since the first point, fraction. */
  cumReturn: number
  /** Drawdown from running peak, fraction <= 0. */
  drawdown: number
}

export type PerformanceSummary = {
  start: EpochMs
  end: EpochMs
  navStart: number
  navEnd: number
  /** Total return as a fraction (0.1 = +10%). */
  totalReturn: number
  /** Max drawdown as a NEGATIVE fraction (-0.2 = -20%). */
  maxDrawdown: number
  bestDayReturn: number | null
  worstDayReturn: number | null
  points: PerformancePoint[]
}

export type PerformanceResponse = { performance: PerformanceSummary | null }

// ---------------------------------------------------------------------------
// GET /api/v1/trades          -> { items, nextCursor }
// GET /api/v1/trades/:id      -> { trade } (with receipts; 404 if unknown)
// ---------------------------------------------------------------------------

export type TradeSide = 'buy' | 'sell'

/** D1 CHECK constraint vocabulary (workers/agent migrations). */
export type TradeStatus =
  | 'planned'
  | 'approved'
  | 'submitted'
  | 'confirmed'
  | 'failed'
  | 'abandoned'

export type Trade = {
  id: string
  workflowId: string | null
  portfolioStateId: string | null
  seq: number
  side: TradeSide
  tokenAddress: Address
  symbol: string | null
  /** Decimal string (exact token quantity). */
  qty: string
  estPriceUsd: number | null
  maxSlippageBps: number
  router: string | null
  status: TradeStatus
  reason: string | null
  createdAt: EpochMs
  updatedAt: EpochMs
}

export type TradesResponse = {
  items: Trade[]
  /** Opaque cursor for the next page; null on the last page. */
  nextCursor: string | null
}

export type TxReceiptStatus = 'pending' | 'confirmed' | 'failed' | 'paper'

export type TxReceipt = {
  id: string
  txHash: string | null
  status: TxReceiptStatus
  blockNumber: number | null
  gasUsed: string | null
  effectiveGasPrice: string | null
  fillPriceUsd: number | null
  fillQty: string | null
  confirmations: number
  updatedAt: EpochMs
}

export type TradeDetail = Trade & { receipts: TxReceipt[] }

export type TradeDetailResponse = { trade: TradeDetail }

// ---------------------------------------------------------------------------
// GET /api/v1/universe/latest -> { snapshot }
// ---------------------------------------------------------------------------

/**
 * Candidate rows as written by the agent indexer (workers/agent/src/db.ts).
 * The gateway passes the JSON array through untyped; fields are treated as
 * optional at render time.
 */
export type UniverseCandidate = {
  address: Address
  name: string
  symbol: string
  decimals: number
  iconUrl: string | null
  holders: number | null
  vol24hUsd: number | null
  source: string
}

export type UniverseSnapshot = {
  id: string
  asOf: EpochMs
  source: string
  candidates: UniverseCandidate[]
  /** Addresses that passed the filter ladder (null until signals run). */
  passed: Address[] | null
}

export type UniverseResponse = { snapshot: UniverseSnapshot | null }

// ---------------------------------------------------------------------------
// GET /api/v1/signals/latest -> { signals }
// (filters/momentum as written by the agent: engine FilterResult rows and
//  momentumForStorage rows — S serialized as null when the gate fails)
// ---------------------------------------------------------------------------

/** Engine filter ladder stage (1 history, 2 volume, 3 liquidity, 4 staleness, 5 anomaly). */
export type FilterStage = 1 | 2 | 3 | 4 | 5

export type FilterMetrics = {
  tradingDayCount: number
  coverage: number
  volMed7: number
  lastTradeAgeHours: number
  zeroVolFrac: number
}

export type FilterResult = {
  /** Lowercased token address. */
  address: Address
  pass: boolean
  /** First stage that failed (absent when pass=true). */
  stage_failed?: FilterStage
  reasons: string[]
  flags: { liquidity_unverified: boolean }
  metrics: FilterMetrics
}

export type MomentumRow = {
  address: Address
  gate: boolean
  /** Composite score; null when the gate fails (serialized -Infinity). */
  S: number | null
  raw: number
  riskAdj: number
  volConfirm: number
  rsi: number | null
  positiveMomentum: boolean
  /** Trading days used. */
  n: number
  volMed7: number
  liquidityUsd: number
}

export type SignalsSnapshot = {
  id: string
  asOf: EpochMs
  universeSnapshotId: string | null
  filters: FilterResult[]
  momentum: MomentumRow[]
  meta: Record<string, unknown> | null
}

export type SignalsResponse = { signals: SignalsSnapshot | null }

// ---------------------------------------------------------------------------
// GET /api/v1/posts -> { items }   (status='posted' only)
// ---------------------------------------------------------------------------

export type XPost = {
  id: string
  refType: 'trade' | 'rebalance'
  refId: string
  content: string
  xPostId: string | null
  postedAt: EpochMs | null
}

export type PostsResponse = { items: XPost[] }

// ---------------------------------------------------------------------------
// GET /api/v1/methodology — static config-derived document. The dashboard
// renders its own /methodology page; this endpoint is exposed for
// completeness and typed loosely.
// ---------------------------------------------------------------------------

export type MethodologyDoc = Record<string, unknown>

// ---------------------------------------------------------------------------
// GET /api/v1/real-broker — read-only view of the REAL deployed StonkBrokers
// contracts on mainnet (4663). Raw payload (no envelope), KV-cached ~10 min.
// Failures are 502 { error, detail } — the gateway never fabricates a
// snapshot when the mainnet RPC is unreachable.
// ---------------------------------------------------------------------------

export type RealBrokerHolding = {
  symbol: string
  address: Address
  /** Human-readable decimal string (decimals-aware). */
  balance: string
  /** Raw base-unit integer as a decimal string. */
  raw: string
  decimals: number
}

export type RealBrokerCompat = {
  /** eth_call executeCall(USDG,0,transfer(holder,0)) with from=holder succeeded. */
  ownerCallOk: boolean
  /** Same simulation with a random non-owner `from` reverted (onlyOwner). */
  strangerCallReverts: boolean
  checkedAt: EpochMs
}

export type RealBrokerResponse = {
  tokenId: number
  nftContract: Address
  wallet: Address
  holder: Address
  holdings: RealBrokerHolding[]
  /** Native ETH balance of the wallet; null when the read failed. */
  eth: { balance: string; raw: string; decimals: number } | null
  compat: RealBrokerCompat
}

// GET /api/v1/holdings — Agentic Stonker #1's own TBA, priced via Birdeye.
export type HoldingRow = {
  symbol: string
  address: Address
  balance: string
  raw: string
  decimals: number
  /** Birdeye spot price in USD; null when unavailable. */
  priceUsd: number | null
  /** balance * priceUsd; null when price unavailable. */
  valueUsd: number | null
}

export type HoldingsResponse = {
  tokenId: number
  nftContract: Address
  wallet: Address
  holder: Address
  holdings: HoldingRow[]
  navUsd: number
  updatedAt: EpochMs
}

// ---------------------------------------------------------------------------
// Admin: GET/PUT /admin/api/config
// Config is a generic key/value store (JSON values). Known keys consumed by
// the agent: mode ('paper'|'live'), paused (bool), kill_switch (bool),
// approval_mode ('auto'|'manual'), confirmations (number),
// max_daily_turnover_frac (number), initial_nav_usd (number),
// engine (partial EngineConfig override object — schema-validated by the
// gateway on PUT; a bad engine override is rejected with 400 {error, detail}).
// ---------------------------------------------------------------------------

export type ConfigItem = {
  key: string
  value: unknown
  version: number
  updatedAt: EpochMs
  updatedBy: string
}

export type ConfigListResponse = { items: ConfigItem[] }

export type ConfigPutResponse = { ok: boolean; key: string; version: number }

// ---------------------------------------------------------------------------
// Admin: POST /admin/api/rebalance and /admin/api/approve proxy the agent's
// internal routes; the gateway forwards the agent's JSON and status code.
// Agent success shapes (workers/agent/src/index.ts):
//   rebalance -> { ok: true, id, status: <workflow status> }
//   approval  -> { ok: true, instanceId, approve }
// Failures are { error, ...extra } with a non-2xx status.
// ---------------------------------------------------------------------------

export type RebalanceResponse = {
  ok?: boolean
  id?: string
  status?: unknown
  error?: string
  [k: string]: unknown
}

export type ApproveResponse = {
  ok?: boolean
  instanceId?: string
  approve?: boolean
  error?: string
  [k: string]: unknown
}

// ---------------------------------------------------------------------------
// Admin: POST /admin/api/pause | /resume | /kill-switch
// ---------------------------------------------------------------------------

export type PauseResumeResponse = { ok: boolean; paused: boolean; version: number }

export type KillSwitchResponse = { ok: boolean; killSwitch: boolean; version: number }

// ---------------------------------------------------------------------------
// Admin: GET /admin/api/audit -> { items }
// ---------------------------------------------------------------------------

export type AuditEntry = {
  id: string
  ts: EpochMs
  actor: string
  action: string
  refType: string | null
  refId: string | null
  detail: unknown
}

export type AuditResponse = { items: AuditEntry[] }

// ---------------------------------------------------------------------------
// Admin: GET /admin/api/health/full
// ---------------------------------------------------------------------------

/** D1 CHECK constraint vocabulary for the health table. */
export type HealthState = 'ok' | 'warn' | 'error'

export type HealthComponent = {
  component: string
  status: HealthState
  detail: unknown
  updatedAt: EpochMs
}

export type AdminHealthResponse = {
  ok: boolean
  db: boolean
  components: HealthComponent[]
  ts: EpochMs
  auth: { actor: string }
}
